import {
  SaveTask_Input,
  useSaveTaskFormQuery,
  useSaveTaskMutation
} from 'generated/graphql';
import { appFormUtil } from 'modules/common';
import React, { useEffect } from 'react';

type IFormState = {
  id: string;
  title: string;
  categoryId: string;
  areaId: string;
  assignedPersonId: string;
  dueDate: string;
  taskDescription: string;
  attachments: {
    id: string;
    fileName: string;
    formattedFileSize: string;
    url: string;
  }[];
};

const initialState: IFormState = {
  id: '',
  title: '',
  categoryId: '',
  areaId: '',
  assignedPersonId: '',
  dueDate: '',
  taskDescription: '',
  attachments: []
};

export function useFormState(id: string) {
  const {
    formState,
    dispatch,
    genFieldProps
  } = appFormUtil.useFormState({
    initialState,
    key2FieldConfig: {
      id: {
        label: 'id',
        type: 'text'
      },
      title: {
        label: 'Title',
        type: 'text'
      },
      categoryId: {
        label: 'Category',
        type: 'text'
      },
      areaId: {
        label: 'Area',
        type: 'text'
      },
      assignedPersonId: {
        label: 'Assignee',
        type: 'text'
      },
      dueDate: {
        label: 'Due Date',
        type: 'date'
      },
      taskDescription: {
        label: 'Task Description',
        type: 'text'
      },
      attachments: {
        label: '',
        type: 'text'
      }
    }
  });

  const { data, loading, called } = useQuery(id);

  useEffect(() => {
    if (data.id !== '' && !loading && called) {
      dispatch({
        type: 'BATCH_UPDATE',
        payload: {
          ...formState,
          ...data
        }
      });
    }
  /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [
    data,
    loading,
    called
  ]);

  return React.useMemo(
    () => {


      return {
        formState,
        dispatch,
        genFieldProps
      };
    },
    [
      dispatch,
      formState,
      genFieldProps
    ]
  );
}

export function useSaveTask() {
  const [
    fnMutate,
    tuple
  ] = useSaveTaskMutation({
    refetchQueries: []
  });

  const fnWrappedMutate = React.useCallback(
    async (input: SaveTask_Input) => {
      await fnMutate({
        variables: {
          input
        }

      });
    },
    [fnMutate]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}

export interface IData extends IFormState {

}

export function useQuery(id: string) {
  const tuple = useSaveTaskFormQuery({
    variables: {
      id
    },
    skip: id === ''
  });

  return React.useMemo(
    () => {
      const data: IData = {
        areaId: tuple.data?.task?.area?.id ?? '',
        assignedPersonId: tuple?.data?.task?.assignee?.id ?? '',
        categoryId: tuple?.data?.task?.category?.id ? tuple?.data?.task?.category.id : '',
        dueDate: tuple?.data?.task?.dueDate ? tuple?.data?.task?.dueDate.toFormat('yyyy-LL-dd') : '',
        attachments: tuple.data?.task?.attachments.map((node) => ({
          id: node.id ?? '',
          fileName: node.fileName ?? '',
          url: node.url,
          formattedFileSize: node.formattedFileSize ?? ''
        })) ?? [],
        id: tuple?.data?.task?.id ?? '',
        taskDescription: tuple?.data?.task?.description ?? '',
        title: tuple?.data?.task?.name ?? ''
      };

      return {
        ...tuple,
        data
      };
    },
    [tuple]
  );
}
